import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { getRecords } from "../../services/api.service";

/**
 * Generic, config driven "smart filter" hook for all master listings.
 *
 * A filter config lives next to the rest of the list config (inside the
 * `listPageData()` of every master `*File.js`) and looks like this:
 *
 *  {
 *    key: "role_id",                 // column of the MAIN model, used in whereCondition
 *    label: "Role",                  // placeholder shown on the control
 *    width: 170,                     // optional control width in px
 *
 *    // (a) static options
 *    options: [{ label: "Material", value: 0 }, { label: "Sparepart", value: 1 }],
 *
 *    // (b) OR options loaded from a master table
 *    source: {
 *      modelName: "role_masters",
 *      labelField: "role_name",
 *      valueField: "id",             // default: "id"
 *      whereCondition: { is_active: true },
 *      orderBy: { field: "role_name", order: "ASC" },
 *    },
 *
 *    // optional cascading: only options whose `parentField` matches the value
 *    // selected in the `dependsOn` filter are shown, and the value is cleared
 *    // whenever the parent changes (Country -> State -> City).
 *    dependsOn: "country_id",
 *    parentField: "country_id",
 *  }
 */

const DEFAULT_LOOKUP_PAGE_SIZE = 1000;

// supports both a plain key ("name") and a dot path into an included
// association ("intake.material_serial_no"), for filters whose label/value
// live on a nested record pulled in via `source.include`.
const getByPath = (record, path) =>
  path?.split(".").reduce((value, key) => value?.[key], record);

const buildOptionsFromRecords = (records, labelField, valueField) =>
  (records || [])
    .map((record) => ({
      label: getByPath(record, labelField) ?? "-",
      value: getByPath(record, valueField),
      raw: record,
    }))
    .filter((option) => option.value !== undefined && option.value !== null);

const useListFilters = (filterConfig) => {
  const config = useMemo(() => filterConfig || [], [filterConfig]);

  // JSON signature so a freshly re-created config array does not re-trigger fetches
  const configSignature = useMemo(
    () =>
      JSON.stringify(
        config.map((item) => ({
          key: item.key,
          source: item.source || null,
          dependsOn: item.dependsOn || null,
          parentField: item.parentField || null,
        }))
      ),
    [config]
  );

  const [filterValues, setFilterValues] = useState({});
  const [remoteOptions, setRemoteOptions] = useState({});
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // reset everything when we move to a different master
  useEffect(() => {
    setFilterValues({});
  }, [configSignature]);

  // load the dropdown data of every remote backed filter
  useEffect(() => {
    const remoteFilters = config.filter((item) => item.source?.modelName);
    if (!remoteFilters.length) {
      setRemoteOptions({});
      return;
    }

    const schema = localStorage.getItem("schema");

    const loadOptions = async () => {
      try {
        setIsOptionsLoading(true);
        const responses = await Promise.all(
          remoteFilters.map(async (item) => {
            const {
              modelName,
              labelField,
              valueField = "id",
              whereCondition,
              orderBy,
              include,
            } = item.source;

            const payload = {
              modelName,
              schema,
              pagination: { page: 1, pageSize: DEFAULT_LOOKUP_PAGE_SIZE },
            };
            if (whereCondition) payload.whereCondition = whereCondition;
            if (orderBy) payload.orderBy = orderBy;
            if (include) payload.include = include;

            try {
              const response = await getRecords(payload, "/getRecords");
              return {
                key: item.key,
                options:
                  response?.code === 1
                    ? buildOptionsFromRecords(
                        response.data,
                        labelField,
                        valueField
                      )
                    : [],
              };
            } catch (error) {
              console.error(`Filter options failed for ${modelName}:`, error);
              return { key: item.key, options: [] };
            }
          })
        );

        if (!mountedRef.current) return;
        setRemoteOptions(
          responses.reduce((acc, item) => {
            acc[item.key] = item.options;
            return acc;
          }, {})
        );
      } finally {
        if (mountedRef.current) setIsOptionsLoading(false);
      }
    };

    loadOptions();
    // config is intentionally excluded - configSignature is its stable identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configSignature]);

  const handleFilterChange = useCallback(
    (key, option) => {
      setFilterValues((previous) => {
        const next = { ...previous, [key]: option || null };
        // clear every filter that cascades (directly or transitively) from this one
        let parents = [key];
        while (parents.length) {
          const cleared = config
            .filter(
              (item) =>
                item.dependsOn && parents.includes(item.dependsOn) && next[item.key]
            )
            .map((item) => item.key);
          cleared.forEach((clearedKey) => {
            next[clearedKey] = null;
          });
          parents = cleared;
        }
        return next;
      });
    },
    [config]
  );

  const resetFilters = useCallback(() => setFilterValues({}), []);

  // options actually shown, after applying any parent/child cascade
  const filters = useMemo(
    () =>
      config.map((item) => {
        const baseOptions = item.options || remoteOptions[item.key] || [];
        const parentValue = item.dependsOn
          ? filterValues[item.dependsOn]?.value
          : undefined;

        const options =
          item.dependsOn && item.parentField && parentValue !== undefined && parentValue !== null
            ? baseOptions.filter(
                (option) => option?.raw?.[item.parentField] === parentValue
              )
            : baseOptions;

        return { ...item, options };
      }),
    [config, remoteOptions, filterValues]
  );

  // where condition sent to /getRecords - only the applied filters are included
  const filterWhereCondition = useMemo(() => {
    const where = {};
    config.forEach((item) => {
      const selected = filterValues[item.key];
      if (!selected || selected.value === undefined || selected.value === null) return;
      if (typeof item.buildWhere === "function") {
        Object.assign(where, item.buildWhere(selected));
      } else {
        where[item.key] = selected.value;
      }
    });
    return where;
  }, [config, filterValues]);

  const appliedFilterCount = useMemo(
    () =>
      config.filter((item) => {
        const selected = filterValues[item.key];
        return selected && selected.value !== undefined && selected.value !== null;
      }).length,
    [config, filterValues]
  );

  return {
    filters,
    filterValues,
    handleFilterChange,
    resetFilters,
    filterWhereCondition,
    appliedFilterCount,
    isOptionsLoading,
  };
};

export default useListFilters;
